// 应用常量定义

const env = (import.meta as { env?: { VITE_API_BASE_URL?: string; VITE_API_URL?: string; VITE_WS_BASE_URL?: string } }).env;

// API配置
export const API_CONFIG = {
  BASE_URL: env?.VITE_API_BASE_URL || env?.VITE_API_URL || '',
  WS_URL: env?.VITE_WS_BASE_URL || '/api/v1/ws',
  TIMEOUT: 600000, // 10分钟
  RETRY_COUNT: 3,
  RETRY_DELAY: 1000, // 1秒
} as const;

// 文件上传配置
export const FILE_UPLOAD_CONFIG = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_FILE_TYPES: [
    'text/markdown',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    '.md',
    '.txt',
    '.docx',
  ],
  CHUNK_SIZE: 1024 * 1024, // 1MB
  MAX_CHUNK_RETRIES: 3,
} as const;

// 设计规范配置
export const DESIGN_CONFIG = {
  DEFAULT_LAYOUT: 'example_1_layout',
  DEFAULT_THEME: 'example_1_theme',
  DEFAULT_TYPOGRAPHY: 'example_1_typography',
  PREVIEW_SIZE: {
    width: '800px',
    height: '450px', // 16:9比例
  },
} as const;

// 页面生成配置
export const GENERATION_CONFIG = {
  BATCH_SIZE: 5,
  PARALLEL_LIMIT: 3,
  QUALITY_LEVELS: ['draft', 'standard', 'premium'] as const,
  DEFAULT_QUALITY: 'standard',
  MAX_RETRY_PER_PAGE: 2,
} as const;

// 工作流配置
export const WORKFLOW_CONFIG = {
  AUTO_SAVE_INTERVAL: 30000, // 30秒
  MAX_HISTORY_ENTRIES: 100,
  STATE_STORAGE_KEY: 'html_ppt_workflow_state',
  SESSION_STORAGE_KEY: 'html_ppt_session',
} as const;

// 错误配置
export const ERROR_CONFIG = {
  MAX_ERROR_DISPLAY: 5,
  ERROR_DISPLAY_DURATION: 5000, // 5秒
  AUTO_RETRY_DELAY: 3000, // 3秒
} as const;

// 本地存储键名
export const STORAGE_KEYS = {
  WORKFLOW_STATE: 'html_ppt_workflow_state',
  USER_PREFERENCES: 'html_ppt_user_preferences',
  RECENT_FILES: 'html_ppt_recent_files',
  DESIGN_TEMPLATES: 'html_ppt_design_templates',
  API_CACHE: 'html_ppt_api_cache',
  DATABASE_STORE: 'html_ppt_database_store',
  OUTLINE_STORE: 'html_ppt_outline_store',
} as const;

// 路由路径
export const ROUTE_PATHS = {
  UPLOAD: '/uploaded',
  DESIGN: '/parsed',
  PAGE_PLAN: '/design-selected',
  GENERATE: '/page-planned',
  EDIT: '/editable',
  PRESENT: '/rendered',
  EXPORT: '/exported',
} as const;

// 页面类型映射
export const PAGE_TYPE_MAP = {
  cover: {
    name: '封面页',
    color: 'bg-indigo-100 text-indigo-800',
    icon: '📖',
  },
  toc: {
    name: '目录页',
    color: 'bg-green-100 text-green-800',
    icon: '📑',
  },
  content: {
    name: '内容页',
    color: 'bg-slate-100 text-slate-800',
    icon: '📄',
  },
  closing: {
    name: '封底页',
    color: 'bg-purple-100 text-purple-800',
    icon: '🏁',
  },
  appendix: {
    name: '附录页',
    color: 'bg-yellow-100 text-yellow-800',
    icon: '📎',
  },
} as const;

// 工作流步骤映射
export const WORKFLOW_STEP_MAP = {
  UPLOADED: {
    name: '文档上传',
    description: '上传并解析文档',
    icon: '📤',
  },
  PARSED: {
    name: '文档解析',
    description: '分析文档结构',
    icon: '🔍',
  },
  DESIGN_SELECTED: {
    name: '设计选择',
    description: '选择设计规范',
    icon: '🎨',
  },
  PAGE_PLANNED: {
    name: '分页规划',
    description: '编辑页面规划',
    icon: '📋',
  },
  GENERATING: {
    name: '生成中',
    description: '生成HTML页面',
    icon: '⚡',
  },
  EDITABLE: {
    name: '可编辑',
    description: '预览和编辑页面',
    icon: '✏️',
  },
  RENDERED: {
    name: '已渲染',
    description: '页面渲染完成',
    icon: '🖼️',
  },
  EXPORTED: {
    name: '已导出',
    description: '导出完成',
    icon: '📤',
  },
} as const;

// 主题颜色
export const THEME_COLORS = {
  primary: {
    light: '#4f46e5',
    dark: '#3730a3',
  },
  secondary: {
    light: '#64748b',
    dark: '#334155',
  },
  success: {
    light: '#10b981',
    dark: '#047857',
  },
  warning: {
    light: '#f59e0b',
    dark: '#d97706',
  },
  error: {
    light: '#ef4444',
    dark: '#dc2626',
  },
  info: {
    light: '#4f46e5',
    dark: '#3730a3',
  },
} as const;

// 响应式断点
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

// 数据库配置
export const DATABASE_CONFIG = {
  // 数据库连接检查间隔（毫秒）
  HEALTH_CHECK_INTERVAL: 30000, // 30秒
  // 自动刷新数据间隔（毫秒）
  AUTO_REFRESH_INTERVAL: 60000, // 60秒
  // 最大重试次数
  MAX_RETRY_COUNT: 3,
  // 重试延迟（毫秒）
  RETRY_DELAY: 1000,
  // 分页大小
  DEFAULT_PAGE_SIZE: 20,
  // 搜索延迟（防抖，毫秒）
  SEARCH_DEBOUNCE: 300,
} as const;

// 导出类型
export type QualityLevel = typeof GENERATION_CONFIG.QUALITY_LEVELS[number];
export type PageTypeKey = keyof typeof PAGE_TYPE_MAP;
export type WorkflowStepKey = keyof typeof WORKFLOW_STEP_MAP;
